import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { isStream } from '../../Services/Redux/Reducers/chatSlice';
import { InputBoxProps } from '../../Services/CommonServices/propTypes';

const InputBox = ({
  input,
  loading,
  inputRef,
  isPlaceHolderVisible,
  isSendButtonDisabled,
  isiOS,
  setHeight,
  setIsSendButtonDisabled,
  handleFocus,
  onHandleInputChange,
  streaming,
  handleStopResponse,
  setIsKeyboardOpen,
  setShowDownArrow
}) => {
  const dispatch = useDispatch();
  const [isSingleLine, setIsSingleLine] = useState(true);
  const [isScrollable, setIsScrollable] = useState(false);
  const [forceReset, setForceReset] = useState(false);
  const hiddenTextareaRef = useRef(null);
  const prevInputRef = useRef('');
  const changedSinceLastInputRef = useRef(false);


  // Create a hidden textarea clone on mount
  useEffect(() => {
    // Create hidden clone for height measurement
    const hiddenTextarea = document.createElement('textarea');
    hiddenTextarea.className = 'inputbox-textarea hidden-clone';
    hiddenTextarea.setAttribute('aria-hidden', 'true');
    hiddenTextarea.style.visibility = 'hidden';
    hiddenTextarea.style.position = 'absolute';
    hiddenTextarea.style.height = 'auto';
    hiddenTextarea.style.top = '-9999px';
    hiddenTextarea.style.left = '-9999px';
    document.body.appendChild(hiddenTextarea);
    hiddenTextareaRef.current = hiddenTextarea;

    // Clean up on unmount
    return () => {
      if (hiddenTextareaRef.current) {
        document.body.removeChild(hiddenTextareaRef.current);
      }
    };
  }, []);

  // Smooth auto-resize textarea based on content
  const adjustTextareaHeight = () => {
    if (!inputRef.current || !hiddenTextareaRef.current) return;

    const textarea = inputRef.current;
    const hiddenTextarea = hiddenTextareaRef.current;
    
    // Handle empty content first - most reliable way
    if (!textarea.value || textarea.value === '' || (isiOS && textarea.value.trim() === '')) {
      textarea.style.height = '34px'; // Base single line height
      setIsScrollable(false);
      setIsSingleLine(true);
      return;
    }
    
    // Copy all styles that affect dimensions
    const computedStyle = window.getComputedStyle(textarea);
    hiddenTextarea.style.width = computedStyle.width;
    hiddenTextarea.style.padding = computedStyle.padding;
    hiddenTextarea.style.border = computedStyle.border;
    hiddenTextarea.style.lineHeight = computedStyle.lineHeight;
    hiddenTextarea.style.fontSize = computedStyle.fontSize;
    hiddenTextarea.style.fontFamily = computedStyle.fontFamily;
    hiddenTextarea.style.boxSizing = 'border-box';
    
    // Copy the content to hidden textarea
    hiddenTextarea.value = textarea.value;
    
    // Calculate height from the clone
    const scrollHeight = hiddenTextarea.scrollHeight;
    
    // Use fixed heights for reliable calculation
    const singleLineHeight = 44; // Base height with padding (confirmed working)
    
    // Fixed heights for 1-6 lines based on testing
    const sixLinesHeight = 154; 
    
    // Determine if content exceeds 6 lines
    const hasExceededMaxLines = scrollHeight > sixLinesHeight;
    
    if (hasExceededMaxLines) {
      // Enable scrolling at exactly 6 lines height
      textarea.style.height = `${sixLinesHeight}px`;
      setIsScrollable(true);
    } else {
      // For content between 1-6 lines - grow smoothly based on actual content
      textarea.style.height = `${scrollHeight}px`;
      setIsScrollable(false);
    }
    
    // Update single line state
    setIsSingleLine(scrollHeight <= singleLineHeight);
  };
  // Function to reset textarea to initial state with better timing
  const resetTextareaHeight = () => {
    // Set a flag to force resize on next render cycle
    setForceReset(true);
   
    // Also apply immediate reset for quicker visual feedback
    if (inputRef.current) {
      inputRef.current.style.height = '34px';
      setIsScrollable(false);
      setIsSingleLine(true);
    }
  };
 
  // Effect to handle forced reset
  useEffect(() => {
    if (forceReset) {
      // Apply reset with slight delay to ensure it happens after React's render
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.style.height = '34px';
          setIsScrollable(false);
          setIsSingleLine(true);
        }
        setForceReset(false);
      }, 10);
    }
  }, [forceReset]);

  // Initialize textarea on mount and handle input changes
  useEffect(() => {
    if (inputRef.current) {
      // Text was cleared - force reset to single line height
       // If input is empty, ensure reset to single line height
       if (!input || input === '') {
        inputRef.current.style.height = '34px';
        setIsScrollable(false);
        setIsSingleLine(true);
    }else {
        adjustTextareaHeight();
      }
      prevInputRef.current = input || '';
    }
  }, [input]);

  // Handle send message - extracted to a reusable function
  const handleSendMessage = () => {
    dispatch(isStream(true));
    streaming(input);
    setHeight(false);
    setIsSendButtonDisabled(true);
    resetTextareaHeight();
  };

  const submitButtonRef = useRef(null);
  // Handle Enter key press - Enhanced for Android compatibility
  const handleKeyDown = (e) => {
    // Check for Enter key using multiple methods for Android compatibility
    const isEnterKey = e.key === 'Enter' || e.code === 'Enter' || e.keyCode === 13 || e.which === 13;
    
    if (isEnterKey && !e.shiftKey) {
      e.preventDefault();
      if (isiOS && (!input || input.trim() === '')) {
        if (inputRef.current) {
          inputRef.current.style.height = '34px';
          setIsScrollable(false);
          setIsSingleLine(true);
        }
        return; // Exit early to prevent any further processing
      }
      setIsKeyboardOpen(false);
      submitButtonRef?.current?.focus();
      if (!isSendButtonDisabled && input?.trim() !== '') {
        handleSendMessage();
        // we've handled submission via keydown, prevent keyup fallback
        changedSinceLastInputRef.current = false;
      }
    }
  };

  // Additional key press handler for Android fallback
  const handleKeyPress = (e) => {
    // Only act if an onChange occurred since the last key event
    if (!changedSinceLastInputRef.current) return;
    
    // Fallback for devices where keyDown may not fire reliably
    const isEnterKey = e.key === "Enter" || e.code === "Enter" || e.keyCode === 13 || e.which === 13;
    if (isEnterKey && !e.shiftKey) {
      // prevent double-submit
      e.preventDefault();
      if (isiOS && (!input || input.trim() === '')) {
        if (inputRef.current) {
          inputRef.current.style.height = '34px';
          setIsScrollable(false);
          setIsSingleLine(true);
        }
        return; // Exit early to prevent any further processing
      }
      if (!isSendButtonDisabled && (input ?? '').trim() !== '') {
        handleSendMessage();
      }
      // reset the flag after handling
      changedSinceLastInputRef.current = false;
    }
  };

  // Custom input change handler to immediately adjust height
  // and sanitize newline characters so Enter doesn't increase content length
  const handleInputChange = (e) => {
    // mark that a change has occurred (so onKeyUp fallback can run)
    changedSinceLastInputRef.current = true;

    const raw = e?.target?.value ?? '';
    // Remove newline characters (CR/LF) — prevents Enter from increasing length
    const sanitized = raw.replace(/\r\n|\n|\r/g, '');
    // If sanitization removed content and the DOM still has newlines, update DOM immediately
    if (sanitized !== raw && inputRef.current) {
      inputRef.current.value = sanitized;
    }

    // Forward a normalized event-like object to parent handler
    onHandleInputChange({ target: { value: sanitized } });

    // Immediate height adjustment for better responsiveness
    if (isiOS && (!sanitized || sanitized.trim() === '')) {
      if (inputRef.current) {
        inputRef.current.style.height = '34px';
        setIsScrollable(false);
        setIsSingleLine(true);
      }
    } else {
      setTimeout(() => adjustTextareaHeight(), 0);
    }
  };

  // Prevent insertion of newline characters via beforeinput (helps iOS virtual keyboards)
  const handleBeforeInput = (e) => {
    const data = e?.nativeEvent?.data ?? '';
    if (!data) return;
    if (/\r|\n/.test(data)) {
      // If current content is empty or whitespace-only, block the insertion
      const current = inputRef?.current?.value ?? input ?? '';
      if (current.trim() === '') {
        e.preventDefault();
      }
    }
  };

  // Prevent pasting only-whitespace/newlines
  const handlePaste = (e) => {
    try {
      const clipboard = e.clipboardData || window.clipboardData;
      const text = clipboard.getData('text') || '';
      if (text.replace(/\s|\n|\r/g, '') === '') {
        e.preventDefault();
      }
    } catch (err) {
      // ignore paste errors
    }
  };

  // Keep the fixed input container above the on-screen keyboard on iOS
  useEffect(() => {
    // Only run viewport-based keyboard adjustments on iOS devices.
    // Use the existing `isiOS` prop if available, otherwise detect iOS via user agent / platform.
    if (typeof window === 'undefined') return;

    const isIOSUserAgent = (() => {
      if (typeof navigator === 'undefined') return false;
      const ua = navigator.userAgent || '';
      const platform = navigator.platform || '';
      // iPhone/iPad/iPod detection or iPadOS on Mac with touch
      return /iP(ad|hone|od)/.test(ua) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1) || /iPad|iPhone|iPod/.test(ua);
    })();

    if (!isiOS && !isIOSUserAgent) return;

    const vv = window.visualViewport;
    const container = document.querySelector('.inputbox-container');

    if (!container || !vv) return; // graceful fallback if visualViewport not supported

    const updatePosition = () => {
      // Only adjust when our textarea is focused
      if (document.activeElement === inputRef.current) {
        const keyboardHeight = Math.max(0, window.innerHeight - vv.height);
        // move the fixed container up by keyboard height
        container.style.bottom = `${keyboardHeight}px`;
      } else {
        container.style.bottom = `0px`;
      }
    };

    vv.addEventListener('resize', updatePosition);
    vv.addEventListener('scroll', updatePosition);
    window.addEventListener('resize', updatePosition);

    // run once to set initial state if already focused
    updatePosition();

    return () => {
      vv.removeEventListener('resize', updatePosition);
      vv.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
      if (container) container.style.bottom = `0px`;
    };
  }, [inputRef, isiOS]);



  return (
    <div className="inputbox-container">
      <div className="inputbox-gradient"></div>
      <div className="inputbox-wrapper">
        <div className="inputbox-inner">
          <textarea
            id="textarea_id"
            enterKeyHint="send"
            className={`inputbox-textarea 
              ${isiOS ? 'inputbox-ios' : ''} 
              ${isScrollable ? 'scrollable' : 'auto-height'} 
              ${isSendButtonDisabled ? 'inputbox-scrollbar' : ''}`}
            ref={inputRef}
            value={input}
            placeholder={isPlaceHolderVisible ? "Send a message" : ""}
            onFocus={handleFocus}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyPress}
            onBeforeInput={handleBeforeInput}
            onPaste={handlePaste}
            onBlur={() => {
              setShowDownArrow(false);
              setTimeout(() => setIsKeyboardOpen(false),200)
            }}
          />
          {loading ? (
            <button
              name="submitMessage"
              className={`inputbox-button ${loading || isSendButtonDisabled ? "disabled" : "enabled"} ${isSingleLine ? 'single-line' : ''}`}
              onClick={() => handleStopResponse(true)}
            >
              <img
                src="/static/assets/stopbtn.svg"
                height="30px"
                width="30px"
                alt="stop"
              />
            </button>
          ) : (
            <button
              ref={submitButtonRef}
              name="submitMessage"
              className={`inputbox-button ${loading || isSendButtonDisabled ? "disabled" : "enabled"} ${isSingleLine ? 'single-line' : ''}`}
              onClick={handleSendMessage}
              disabled={isSendButtonDisabled}
            >
              <img
                src={
                  isSendButtonDisabled
                    ? "/static/assets/submit_disabled.svg"
                    : "/static/assets/submit_enabled.svg"
                }
                height="30px"
                width="30px"
                alt="arrow"
              />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

InputBox.propTypes = InputBoxProps;

export default InputBox;
